<template>
    <div>
        <form class="max-w-xs mx-auto" ref="form" @submit.prevent="register">
            <div class="form-group mb-3">
                <input
                    :class="{ 'auth-input': true, 'is-invalid': errors.has('name') }"
                    :disabled="isLoggedIn()"
                    name="name"
                    v-validate="{ required: true, min: 5 }"
                    :placeholder="translate('auth_register_placeholder_name')"
                    v-model="form.name"
                    type="text"
                />
                <div class="invalid-feedback">
                    <span v-if="errors.has('name')" class="has-error text-center">{{ errors.first('name') }}</span>
                </div>
            </div>

            <div class="form-group mb-3">
                <input
                    :class="{ 'auth-input': true, 'is-invalid': errors.has('email') }"
                    :disabled="isLoggedIn()"
                    v-validate="'required'"
                    name="email"
                    :placeholder="translate('auth_register_placeholder_email')"
                    v-model="form.email"
                    type="email"
                />
                <div class="invalid-feedback">
                    <span v-if="errors.has('email')" class="has-error text-center">{{ errors.first('email') }}</span>
                </div>
            </div>

            <div v-if="devMode" class="form-group mb-3">
                <input
                    :class="{ 'auth-input': true, 'is-invalid': errors.has('password') }"
                    :disabled="isLoggedIn()"
                    v-validate="'required|min:6'"
                    name="password"
                    :placeholder="translate('auth_register_placeholder_password')"
                    v-model="form.password"
                    type="password"
                />
                <div class="invalid-feedback">
                    <span v-if="errors.has('password')" class="has-error text-center">{{
                        errors.first('password')
                    }}</span>
                </div>
            </div>

            <div class="select-auth-box flex mb-4">
                <x-checkbox
                    id="otp-radio"
                    type="radio"
                    class="mr-1 shadow-text-none font-size-14"
                    size="md"
                    theme="dark"
                    name="select-auth"
                    :checked="true"
                    @input="() => (form.auth_select = 'otp')"
                >
                    {{ translate('auth_login_with_otp') }}
                </x-checkbox>
                <x-checkbox
                    id="auth-radio"
                    type="radio"
                    class="mr-1 shadow-text-none font-size-14"
                    size="md"
                    theme="dark"
                    name="select-auth"
                    @input="() => (form.auth_select = 'google-auth')"
                >
                    {{ translate('auth_login_with_2fa') }}
                </x-checkbox>
            </div>

            <div class="form-group mb-5">
                <button class="auth-input button button-success" :disabled="!readyToRegister || isLoggedIn()">
                    {{ translate('auth_register_button_register') }}
                </button>
            </div>

            <div class="form-group text-center">
                <a class="text-sm no-underline text-black shadow-text-none" @click.prevent="toggleLogin" href="#">{{
                    translate('auth_button_back_login')
                }}</a>
            </div>
        </form>
    </div>
</template>

<script>
import appConfig from '@/app.config.js'
import Request from '@/lib/request.js'
import popup from '@/lib/popup.js'
import { isLoggedIn } from '@/lib/user.js'
import { load } from 'recaptcha-v3'
import helper from '@/lib/helper.js'
import helperMixins from '@/mixins/helper.js'
import XCheckbox from '@/components/v2/XCheckbox'

export default {
    name: 'RegisterForm',

    components: {
        XCheckbox
    },

    data() {
        return {
            form: {
                name: '',
                email: '',
                password: '',
                create_from: window.location.pathname,
                captcha_response: '',
                auth_select: 'otp'
            }
        }
    },

    mixins: [helperMixins],

    computed: {
        readyToRegister() {
            if (this.devMode) {
                return this.form.name.length > 0 && this.form.email.length > 0 && this.form.password.length > 0
            }

            return this.form.name.length > 0 && this.form.email.length > 0
        }
    },

    methods: {
        isLoggedIn() {
            return isLoggedIn()
        },

        reset() {
            this.form = {
                name: '',
                email: '',
                password: '',
                captcha_response: '',
                auth_select: 'otp'
            }
        },

        toggleLogin() {
            this.$emit('toggle-login')
        },

        async register() {
            const validateResult = await this.$validator.validate()

            if (!validateResult) {
                this.$emit('validate-fail')
                return false
            }

            const recaptcha = await load(this.getRecaptchaSiteKey(), {
                useRecaptchaNet: true,
                autoHideBadge: true
            })

            this.$emit('try-register')

            this.form.captcha_response = await recaptcha.execute('validate_captcha', { action: 'validate_captcha' })

            const request = new Request()

            if (this.devMode) {
                try {
                    const res = await request.post(appConfig.api.register, this.form)
                    helper.gTrackEvent('register', {
                        event_category: 'Register',
                        event_label: 'Form_register',
                        transport_type: 'beacon'
                    })

                    this.$emit('registered', res.data)
                } catch (err) {
                    const res = err.response.data.data
                    this.$emit('fail')

                    if (res.email) {
                        popup.alert(this.translate('auth_alert_already_email'))
                    }
                } finally {
                    this.reset()
                }
            } else {
                try {
                    const res = await request.get(appConfig.api.validateRegisterOtp, { params: this.form })

                    this.form.is_google2fa = res.data.data.auth_select == 'otp' ? false : true
                    this.form.key_google2fa = res.data.data.key_google2fa
                    this.form.qrcode_image = res.data.data.qrcode_image

                    if (this.isSchoolPage() && !res.data.data.is_valid_invite_token) {
                        this.$emit('open-confirm-to-teacher', this.form)
                        return
                    }

                    this.$emit('register-otp', this.form)
                } catch (err) {
                    const res = err.response.data.data

                    this.$emit('fail')

                    if (res.email) {
                        popup.alert(this.translate('auth_alert_already_email'))
                    }
                }
            }
        },

        getRecaptchaSiteKey() {
            return process.env.VUE_APP_RECAPTCHA_SITE_KEY
        }
    }
}
</script>

<style lang="scss">
.select-auth-box {
    column-gap: 16px;
}

.font-size-14 {
    font-size: 14px;
}
</style>
